import React, { useState } from 'react';
import { View, Text, TextInput, Pressable, ActivityIndicator, Platform } from 'react-native';
import { signInStyles } from '../../src/signIn/styles/signIn.styles';
import { SignInAuthScreenLayout } from '../../src/signIn/components/SignInAuthScreenLayout';
import { verificationFormStyles as vf } from './VerificationForm.styles';
import { MAX_PASSWORD_LENGTH } from '../../src/signIn/helpers/signInApi';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';

interface PasswordResetFormProps {
  email: string;
  loading?: boolean;
  /** Server/validation error from the reset handler (invalid code, weak password, expired code). */
  error?: string;
  onSubmit: (code: string, newPassword: string) => void;
  onResend?: () => void;
  resendLoading?: boolean;
  onBack: () => void;
  testID?: string;
}

const SIGN_IN_PLACEHOLDER_COLOR = '#5A5A60';
const SIGN_IN_ACCENT_SPINNER_COLOR = '#A78BFA';

export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
  email,
  loading = false,
  error,
  onSubmit,
  onResend,
  resendLoading = false,
  onBack,
  testID,
}) => {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Local-only error for client checks that don't need a round-trip (passwords don't match).
  const [localError, setLocalError] = useState('');

  // SECURITY: code is digits-only, max 6.
  const handleCodeChange = (text: string) => {
    setLocalError('');
    setCode(text.replace(/[^0-9]/g, '').slice(0, 6));
  };

  const handleSubmit = () => {
    setLocalError('');
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }
    onSubmit(code, password);
  };

  const shownError = localError || error;
  const canSubmit = code.length === 6 && !!password && !!confirmPassword;

  return (
    <SignInAuthScreenLayout testID={testID || 'password-reset-form'}>
      <View
        style={[signInStyles.card, vf.verificationCard]}
        testID={testID ? `${testID}-card` : 'password-reset-form-card'}
      >
        <Text style={signInStyles.title}>Reset Password</Text>
        <Text style={signInStyles.subtitle}>{`We sent a 6-digit code to ${email}`}</Text>
        <Text style={vf.verificationHint}>
          Enter the code from the email, then choose a new password.
        </Text>

        <View style={signInStyles.inputContainer}>
          <TextInput
            style={[signInStyles.input, shownError ? signInStyles.inputError : undefined]}
            placeholder="6-digit code"
            placeholderTextColor={SIGN_IN_PLACEHOLDER_COLOR}
            value={code}
            onChangeText={handleCodeChange}
            autoCapitalize="none"
            keyboardType="number-pad"
            maxLength={6}
            autoFocus
            testID={testID ? `${testID}-code-input` : 'password-reset-form-code-input'}
          />
        </View>

        <View style={signInStyles.inputContainer}>
          <TextInput
            style={[signInStyles.input, shownError ? signInStyles.inputError : undefined]}
            placeholder="New Password"
            placeholderTextColor={SIGN_IN_PLACEHOLDER_COLOR}
            value={password}
            onChangeText={setPassword}
            secureTextEntry
            autoCapitalize="none"
            autoComplete="password-new"
            maxLength={MAX_PASSWORD_LENGTH}
            testID={testID ? `${testID}-password-input` : 'password-reset-form-password-input'}
          />
        </View>

        <View style={signInStyles.inputContainer}>
          <TextInput
            style={[signInStyles.input, shownError ? signInStyles.inputError : undefined]}
            placeholder="Confirm New Password"
            placeholderTextColor={SIGN_IN_PLACEHOLDER_COLOR}
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            secureTextEntry
            autoCapitalize="none"
            autoComplete="password-new"
            maxLength={MAX_PASSWORD_LENGTH}
            testID={testID ? `${testID}-confirm-input` : 'password-reset-form-confirm-input'}
          />
          {shownError ? (
            <View style={signInStyles.errorContainer}>
              <Text style={signInStyles.errorText}>{shownError}</Text>
            </View>
          ) : null}
        </View>

        <Pressable
          style={({ pressed, hovered }) => [
            signInStyles.button,
            signInStyles.primaryButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE as never }),
            pressed && signInStyles.primaryButtonPressed,
            hovered && Platform.OS === 'web' && signInStyles.primaryButtonHover,
          ]}
          onPress={handleSubmit}
          disabled={loading || !canSubmit}
          testID={testID ? `${testID}-submit-button` : 'password-reset-form-submit-button'}
        >
          {loading
            ? <ActivityIndicator color={SIGN_IN_ACCENT_SPINNER_COLOR} />
            : <Text style={signInStyles.buttonText}>Reset Password</Text>
          }
        </Pressable>

        {onResend ? (
          <Pressable
            style={({ pressed }) => [signInStyles.linkButton, pressed && signInStyles.linkButtonPressed]}
            onPress={onResend}
            disabled={resendLoading}
            testID={testID ? `${testID}-resend-button` : 'password-reset-form-resend-button'}
          >
            {resendLoading
              ? <ActivityIndicator size="small" color={SIGN_IN_ACCENT_SPINNER_COLOR} />
              : <Text style={signInStyles.linkText}>Resend code</Text>
            }
          </Pressable>
        ) : null}

        <Pressable
          style={({ pressed }) => [signInStyles.linkButton, pressed && signInStyles.linkButtonPressed]}
          onPress={onBack}
          testID={testID ? `${testID}-back-button` : 'password-reset-form-back-button'}
        >
          <Text style={signInStyles.linkText}>Back to Sign In</Text>
        </Pressable>
      </View>
    </SignInAuthScreenLayout>
  );
};
